import React from "react";
import { dk } from "./loginDark";

const LogoMark: React.FC = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden>
    <circle cx="24" cy="24" r="23" stroke={dk.hairline} strokeWidth="1" />
    <circle
      cx="24"
      cy="24"
      r="23"
      stroke={dk.sageMid}
      strokeWidth="1"
      strokeDasharray="4 8"
      opacity="0.4"
      style={{
        transformOrigin: "24px 24px",
        animation: "logoSpin 20s linear infinite",
      }}
    />
    {/* mother arc */}
    <path
      d="M14 28 C14 18 34 18 34 28"
      stroke={dk.sageBright}
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
    {/* child circle */}
    <circle cx="24" cy="22" r="6" fill={dk.sage} opacity="0.25" />
    <circle
      cx="24"
      cy="22"
      r="6"
      stroke={dk.sageMid}
      strokeWidth="1.2"
      fill="none"
    />
    {/* leaf accents */}
    <path
      d="M24 10 C27 15 27 20 24 22 C21 20 21 15 24 10Z"
      fill={dk.sage}
      opacity="0.7"
    />
    <path
      d="M24 34 C21 29 21 24 24 22 C27 24 27 29 24 34Z"
      fill={dk.sage}
      opacity="0.35"
    />
    {/* glow dot */}
    <circle cx="24" cy="22" r="2.5" fill={dk.sageBright} />
    <circle cx="24" cy="22" r="4" fill={dk.sageBright} opacity="0.2" />
  </svg>
);

interface Props {
  appName: string;
}

export const LoginLeftPanel: React.FC<Props> = ({ appName }) => (
  <section
    className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between"
    style={{ background: dk.bg }}
  >
    {/* background orbs */}
    <svg
      className="drift pointer-events-none absolute -left-32 top-0 h-[600px] w-[600px]"
      viewBox="0 0 600 600"
      aria-hidden
    >
      <defs>
        <radialGradient id="orb1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={dk.sageMid} stopOpacity="0.18" />
          <stop offset="100%" stopColor={dk.sageMid} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="300" cy="300" r="280" fill="url(#orb1)" />
    </svg>
    <svg
      className="drift-slow pointer-events-none absolute -right-20 bottom-0 h-[480px] w-[480px]"
      viewBox="0 0 480 480"
      aria-hidden
    >
      <defs>
        <radialGradient id="orb2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={dk.sage} stopOpacity="0.12" />
          <stop offset="100%" stopColor={dk.sage} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="240" cy="240" r="220" fill="url(#orb2)" />
    </svg>

    {/* grid lines */}
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.04]"
      aria-hidden
    >
      <defs>
        <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
          <path
            d="M 48 0 L 0 0 0 48"
            fill="none"
            stroke={dk.ink}
            strokeWidth="0.5"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>

    {/* brand header */}
    <header className="relative z-10 flex items-center gap-3 p-10 rise rise-1">
      <LogoMark />
      <div className="ml-1">
        <p
          className="text-[10px] uppercase tracking-[0.34em]"
          style={{ color: dk.muted }}
        >
          Admin Console
        </p>
        <p className="fraunces text-[15px] italic" style={{ color: dk.ink }}>
          {appName}
        </p>
      </div>
    </header>

    {/* headline */}
    <div className="relative z-10 max-w-xl px-10 pb-12">
      <p
        className="rise rise-2 mb-5 text-[10px] uppercase tracking-[0.38em]"
        style={{ color: dk.muted }}
      >
        Est. 2026 &nbsp;·&nbsp; A quiet ledger of care
      </p>
      <h1
        className="fraunces rise rise-3 text-[62px] leading-[0.93] tracking-[-0.018em] xl:text-[72px]"
        style={{ color: dk.ink }}
      >
        Where the
        <br />
        <em style={{ color: dk.sageBright }}>softest</em> days
        <br />
        are tended.
      </h1>
      <p
        className="rise rise-4 mt-8 max-w-md text-[14px] leading-relaxed"
        style={{ color: dk.inkSoft }}
      >
        Sign in to orchestrate centres, caregivers, and the rituals that carry
        mothers through their earliest mornings.
      </p>
      <div className="rise rise-5 mt-12 flex items-center gap-5">
        <span className="h-px w-12" style={{ background: dk.hairline }} />
        <span
          className="fraunces italic text-[13px]"
          style={{ color: dk.muted }}
        >
          &ldquo;Care is a practice, not a product.&rdquo;
        </span>
      </div>
    </div>
  </section>
);
